import { useContext, useState } from 'react';
import { matchPath, NavLink } from 'react-router-dom';
import classNames from 'classnames';

import { UserContext } from '../../App';
import Button from '../Button/Button';
import { AppRoute, ButtonType, PlanType } from '../../types/enums';
import { handleLogout } from '../../utils/auth';
import useMenuItems from '../../hooks/useMenuItems';

import logo from './monitor-monk-logo.png';
import { ReactComponent as CloseMenu } from './close.svg';
import { ReactComponent as OpenMenu } from './bars-solid.svg';
import { ReactComponent as Arrow } from './chevron-down-solid.svg';

import styles from './Header.module.scss';

const Header = () => {
  const { user, setUser } = useContext(UserContext);
  const menuItems = useMenuItems();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const onLogout = () => {
    handleLogout();
    setUser(undefined);
  };

  return (
    <header className={styles.header}>
      <div className="container">
        <NavLink to={user ? AppRoute.WEBSITES : AppRoute.LOGIN}>
          <img src={logo} alt="MonitorMonk" />
        </NavLink>
        {user && (
          <>
            <div className={styles.desktopMenu}>
              {Object.values(menuItems).map(({ name, url }) => (
                <NavLink
                  key={url}
                  className={classNames(styles.desktopMenuItem, {
                    [styles.desktopActiveMenuItem]: matchPath(location.pathname, url)
                  })}
                  to={url}>
                  {name}
                </NavLink>
              ))}
            </div>
            <div className={styles.mobileMenuOpener} onClick={() => setIsMobileMenuOpen(true)}>
              <OpenMenu />
            </div>
            <div className={styles.rightMenu}>
              <div className={styles.nameWrapper}>
                {user.plan.name === PlanType.FREE && (
                  <NavLink to={AppRoute.UPGRADE}>
                    <Button spaceSaving small type={ButtonType.PRIMARY}>
                      <div>Upgrade to premium</div>
                    </Button>
                  </NavLink>
                )}
                <div className={styles.name} onClick={() => setShowLogout((show) => !show)}>
                  {user.name
                    .split(' ')
                    .slice(0, 2)
                    .map((it) => it[0])
                    .join('')}
                </div>
                {showLogout && (
                  <div className={styles.logout}>
                    <div>
                      <small style={{ fontSize: '0.7rem' }}>Logged in as</small>
                      <br />
                      {user.name}
                    </div>
                    <NavLink to={AppRoute.PROFILE}>Profile</NavLink>

                    <NavLink to={AppRoute.UPGRADE}>Plans</NavLink>

                    <NavLink to={AppRoute.CONTACT_US}>Contact us</NavLink>

                    <div onClick={onLogout}>Sign Out</div>
                  </div>
                )}
              </div>
            </div>
            <div
              className={classNames(styles.mobilMenu, {
                [styles.mobilMenuOpen]: isMobileMenuOpen
              })}>
              <div onClick={() => setIsMobileMenuOpen(false)} className={styles.closeMobilMenu}>
                <CloseMenu />
              </div>
              <div className={styles.name}>{user.name}</div>
              {Object.values(menuItems).map(({ name, url }) => (
                <div className={styles.menuItem} key={url}>
                  <NavLink
                    className={classNames(styles.menuLink, {
                      [styles.activeMenuItem]: matchPath(location.pathname, url)
                    })}
                    to={url}>
                    {name}
                  </NavLink>
                </div>
              ))}
              <div className={styles.menuItem} key={AppRoute.PROFILE}>
                <NavLink
                  className={classNames(styles.menuLink, {
                    [styles.activeMenuItem]: matchPath(location.pathname, AppRoute.PROFILE)
                  })}
                  to={AppRoute.PROFILE}>
                  Profile
                </NavLink>
              </div>
              <div className={styles.menuItem} key={AppRoute.CONTACT_US}>
                <NavLink
                  className={classNames(styles.menuLink, {
                    [styles.activeMenuItem]: matchPath(location.pathname, AppRoute.CONTACT_US)
                  })}
                  to={AppRoute.CONTACT_US}>
                  Contact us
                </NavLink>
              </div>
              <Button action={onLogout} type={ButtonType.SECONDARY}>
                Sign out
              </Button>
            </div>
            {isMobileMenuOpen && <div className={styles.menuOverlay} />}
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
